import * as ngCore from '@angular/core'
import { isDeepEqual } from '@attain-apps/data-utils'
import type * as ents from '@attain-apps/entities'
import { TypeChecker } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import * as me from '../me'

import * as scenarios from './scenarios'

/**
 * Route-bound scenario context helpers.
 *
 * This is intentionally not the generic app-scope context. Some apps have no
 * scenario concept at all, and those apps should not inject anything from this
 * module. Scenario-based collections use this route-bound observable when they
 * need the current scenario for the active app.
 */

/**
 * Scenario context value visible inside a scenario-based route scope.
 */
export type Context = ents.core.admin.Context.ForScenario
export namespace Context {
  /**
   * Scenario context stream visible inside a scenario-based route scope.
   *
   * The type allows `undefined` because entity collections can model "no
   * context" as a valid state. Feature effects can also use that emission to
   * clear state that was loaded for a previously selected scenario.
   */
  export type Stream = rxjs.Observable<Context | undefined>

  /**
   * Injection token for the scenario context stream in this route scope.
   */
  export const TOKEN = new ngCore.InjectionToken<Stream>(
    'RouteBound.Scenario.Context',
  )

  /**
   * Provides scenario context for one app route injector.
   *
   * This does not mean every app has scenarios. It only makes the scoped token
   * available; Angular creates the observable lazily if a scenario-based
   * collection or feature injects it.
   */
  export function provide(): ngCore.Provider {
    return {
      provide: TOKEN,
      useFactory: create$,
    }
  }

  type App = ents.core.admin.App.ForUser
  type AppContext = ents.core.admin.Context.ForApp
  type Scenario = ents.core.admin.Scenario
  type Pair = readonly [
    App | undefined,
    AppContext | undefined,
    Scenario | undefined,
  ]

  /**
   * Builds a scenario context stream from root context selectors.
   */
  function create$(): Stream {
    const meFeature = ngCore.inject(me.Feature)
    const scenariosFeature = ngCore.inject(scenarios.Feature)

    return rxjs
      .combineLatest([
        meFeature.contexts.selectors.app.asObservable,
        meFeature.contexts.selectors.appContext.asObservable,
        scenariosFeature.selectors.currentScenario.asObservable,
      ])
      .pipe(
        // Navigation can update the app selector, app context, and route-bound
        // scenario selector at slightly different times. Emit undefined while
      // those pieces are temporarily unresolved so consumers can cancel work
        // or clear stale scenario-owned state.
        rxjs.map((pair: Pair): Context | undefined => {
          const [app, appContext, scenario] = pair

          if (
            !TypeChecker.isNotNullish(app) ||
            !TypeChecker.isNotNullish(appContext) ||
            !TypeChecker.isNotNullish(scenario) ||
            app.id !== appContext.appId
          ) {
            return undefined
          }

          // Freshly queried route-bound scenarios can drive scenario context
          // without rewriting the root `me` snapshot.
          return {
            ...appContext,
            scenarioId: scenario.id,
          }
        }),
        rxjs.distinctUntilChanged((a, b) => isDeepEqual(a, b)),
      )
  }
}
